/**
 * Global "action requests in flight" counter.
 *
 * An action-button request (plugin_action call, Save, Migrate deprecations,
 * run/trigger, delete, bundle/custom-object actions, …) increments this counter
 * while its XHR is in flight and decrements it when the request settles. The
 * count is the signal the ActionInFlightGuard uses to block tab-close / in-app
 * navigation until the request has returned — so an action fired just before the
 * user closes the tab is guaranteed to reach the backend (its execution gets
 * created) instead of being aborted mid-flight.
 *
 * The increment/decrement is driven centrally from the axios request/response
 * interceptors in api/restClient.ts, keyed on a `trackAction: true` request
 * config flag, so it tracks the true XHR lifetime.
 */
export const actions = {
    INCREMENT_PENDING_ACTION: "INCREMENT_PENDING_ACTION",
    DECREMENT_PENDING_ACTION: "DECREMENT_PENDING_ACTION",
    RESET_PENDING_ACTIONS: "RESET_PENDING_ACTIONS",
} as const;

export interface PendingActionsState {
    count: number;
}

export const initialState: PendingActionsState = {
    count: 0,
};

interface PendingActionsAction {
    type: string;
}

export default (
    state: PendingActionsState = initialState,
    { type }: PendingActionsAction = { type: "" }
): PendingActionsState => {
    switch (type) {
        case actions.INCREMENT_PENDING_ACTION:
            return { ...state, count: state.count + 1 };
        case actions.DECREMENT_PENDING_ACTION:
            // Clamp at 0 — a stray decrement (e.g. a settle after a store reset)
            // must never push the count negative and silently disarm the guard.
            return { ...state, count: Math.max(0, state.count - 1) };
        case actions.RESET_PENDING_ACTIONS:
            return { ...state, count: 0 };
        default:
            return state;
    }
};
